import { SearchspringSearchProvider } from 'libs/searchspring/fetchers/search'

import clsx from 'clsx'

import { useEffect, useState } from 'preact/hooks'
import { useScrollCache } from 'libs/searchspring/hooks/useScrollCache'
import { useFilterScroll } from 'libs/searchspring/hooks/useFilterScroll'
import { useScrollPositionKeeper } from 'libs/searchspring/hooks/useScrollPositionKeeper'
import { scrollHistoryManager } from 'helpers/scroll-history'
import { useInfiniteScrollResults } from 'libs/searchspring/hooks/useInfiniteScrollResults'
import { InfiniteScrollNext, InfiniteScrollPrevious } from 'libs/searchspring/components/collection/InfiniteScroll'
import { useOption } from 'libs/searchspring/hooks/useOption'
import { FiltersProvider, useFilters } from 'libs/searchspring/hooks/useFilters'
import { CollectionToolbar } from 'libs/searchspring/components/collection/CollectionToolbar'
import { FilterDrawer } from 'libs/searchspring/components/collection/filters/FilterDrawer'
import { SearchHeader } from 'libs/searchspring/components/SearchHeader'
import { ResultItem } from 'libs/searchspring/components/ResultCardWrapper'
import { SkeletonCard } from 'libs/searchspring/components/SkeletonCard'

function SearchGrid({ showFilters }) {
  history.scrollRestoration = 'manual'
  return (
    <FiltersProvider>
      <SearchGridBody showFilters={showFilters} />
    </FiltersProvider>
  )
}

function SearchGridBody({ showFilters }) {
  const [firstLoad, setFirstLoad] = useState(true)
  const [renderSkeleton, setRenderSkeleton] = useState(true)
  const [skeletonVisible, setSkeletonVisible] = useState(true)
  const { allResults, gridChange, isLoading, results } = useInfiniteScrollResults()
  const { initialFilters } = useFilters()
  const { setEntry } = useScrollPositionKeeper(gridChange)
  const { setScrollElement } = useFilterScroll()
  const query = useOption(
    'query',
    { syncWithURL: true }
  )

  const { corrected = '', original = '' } = results?.query || {}
  const didYouMean = results?.didYouMean?.query || ''

  const corrections = {
    corrected,
    original,
    didYouMean
  }

  useEffect(() => {
    if (!isLoading) setFirstLoad(false)
  }, [isLoading])

  useEffect(() => {
    if (isLoading || firstLoad) {
      setRenderSkeleton(true)
      setSkeletonVisible(true)
      return
    }
    setSkeletonVisible(false)
    const timer = setTimeout(() => setRenderSkeleton(false), 400)
    return () => clearTimeout(timer)
  }, [isLoading, firstLoad])

  useScrollCache([allResults, firstLoad])

  const totalResults = results?.pagination?.totalResults || 0
  const hasFilters = initialFilters.length > 0
  const hasResults = allResults?.length > 0
  const hideFilterControls = !firstLoad && !isLoading && (!hasFilters || !hasResults)

  return (
    <div className="grow flex flex-col">
      <SearchHeader query={query} totalResults={totalResults} isLoading={isLoading} corrections={corrections}/>
      {!hideFilterControls && (
        <div className={clsx('transition-opacity duration-300', firstLoad && isLoading && 'pointer-events-none opacity-60')} aria-busy={firstLoad && isLoading}>
          <CollectionToolbar />
        </div>
      )}
      <div className="grow flex flex-col md:grid md:grid-cols-min-full md:px-margin">
        {!hideFilterControls && (
          <div className={clsx('transition-opacity duration-300', firstLoad && isLoading && 'pointer-events-none opacity-60')} aria-busy={firstLoad && isLoading}>
            <FilterDrawer showFilters={showFilters} />
          </div>
        )}
        <div className="grow flex flex-col">
          <div
            ref={(el) => { setScrollElement(el); setEntry(el) }}
            className={clsx(
              'grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 xxxl:grid-cols-5 [grid-auto-flow:row_dense] gap-x-gutter gap-y-2 w-full lg:pt-2 transition-[min-height] duration-[800ms] ease-out',
              renderSkeleton ? 'min-h-screen-height' : 'min-h-0'
            )}
          >
            <InfiniteScrollPrevious />
            {renderSkeleton ? (
              Array.from({ length: window.settings?.per_page || 24 }, (_, index) => (
                <SkeletonCard
                  key={`skeleton-${index}`}
                  className={clsx('w-full transition-opacity duration-[400ms] ease-out', !skeletonVisible && 'opacity-0')}
                />
              ))
            ) : (
              allResults?.map((item, index) => (
                <ResultItem item={item} key={item.handle} className="wk-wishlist-button-target" dataIndex={index.toString()} />
              ))
            )}
          </div>
          {!firstLoad && allResults.length > 0 && (
            <div className="flex justify-center px-margin py-3.5">
              <InfiniteScrollNext />
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export function WrappedSearchGrid({ 'show-filters': showFilters }) {
  scrollHistoryManager.updateHistoryState()
  const urlParams = new URLSearchParams(window.location.search)
  const queryParam = urlParams.get('query')
  return (
    <SearchspringSearchProvider query={queryParam}>
      <SearchGrid showFilters={showFilters === 'true'} />
    </SearchspringSearchProvider>
  )
}
